import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import { FactSourceImage } from './fact-source-image';
import { useGetFact } from '@/api/queries/useGetFact';
import { Fact } from '@/api/types/backend-dto-types';
import { ToastDrawer } from '@/new/components/drawers/toast-drawer';
import { useStore } from '@/store';

interface FactReferenceProps {
  factId: string;
  referenceNumber: number;
  caseId: string;
  onFactClick?: (factId: string) => void;
}

interface FactDetailContentProps {
  fact: Fact;
}

const FactDetailContent = ({ fact }: FactDetailContentProps) => {
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);

  const validLocations =
    fact.locations?.filter((location) => location.boundingBoxes && location.boundingBoxes.length > 0) ?? [];

  const currentLocation = validLocations[currentSourceIndex];

  const handlePrevSource = () => {
    setCurrentSourceIndex((prev) => (prev > 0 ? prev - 1 : validLocations.length - 1));
  };

  const handleNextSource = () => {
    setCurrentSourceIndex((prev) => (prev < validLocations.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center justify-between pr-8 font-bold">
        <div className="text-xs text-gray-700">{fact.factDate ? new Date(fact.factDate).toLocaleDateString() : ''}</div>
      </div>

      <div className="flex-1">
        <p className="text-xs leading-relaxed text-gray-700">{fact.description}</p>
      </div>

      {currentLocation && (
        <FactSourceImage
          location={currentLocation}
          showOpenButton={true}
          currentSourceIndex={currentSourceIndex}
          totalSources={validLocations.length}
          onPrevSource={handlePrevSource}
          onNextSource={handleNextSource}
        />
      )}
    </div>
  );
};

const TranscriptionFactReference = ({ factId, referenceNumber, caseId, onFactClick }: FactReferenceProps) => {
  const { popupFactFromTranscription, setPopupFactFromTranscription } = useStore();
  const { matterId } = useParams();

  const isThisFactOpen = popupFactFromTranscription?.factId === factId && popupFactFromTranscription?.caseId === caseId;

  const { data: fact, isLoading } = useGetFact(matterId ?? '', factId);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isThisFactOpen) {
      setPopupFactFromTranscription(null);
    } else {
      setPopupFactFromTranscription({ factId, caseId });
    }

    onFactClick?.(factId);
  };

  const handleCloseDrawer = () => {
    setPopupFactFromTranscription(null);
  };

  const isLoadingState = isThisFactOpen && isLoading;
  const showDrawer = isThisFactOpen && fact;

  return (
    <>
      <span
        onClick={handleClick}
        className={`mx-0.5 inline-flex cursor-pointer items-center justify-center rounded border px-1.5 py-0.5 text-xs font-medium transition-colors duration-150 ${
          isLoadingState
            ? 'border-gray-200 bg-gray-50 text-gray-500'
            : 'border-blue-200 bg-blue-50 text-blue-600 hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800'
        }`}
        title={
          fact ? `Click to view details for fact: ${fact.description?.substring(0, 50) ?? ''}...` : `Fact ID: ${factId}`
        }
      >
        {isLoadingState ? '...' : referenceNumber}
      </span>

      <ToastDrawer
        isOpen={isThisFactOpen}
        toggleDrawer={handleCloseDrawer}
        position="left"
        width="42%"
        obscureBackground={false}
        maxHeight="80%"
      >
        {showDrawer && <FactDetailContent fact={fact} />}
        {isLoadingState && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">Loading fact details...</div>
          </div>
        )}
      </ToastDrawer>
    </>
  );
};

export default TranscriptionFactReference;
